import { useCallback, useMemo, useState } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ApiErrors, useApprovePaymentInstructionMutation, useReprovePaymentInstructionMutation, } from '@ibk/pagamentos';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { localStorageService, paymentDrawerErrorAtom, paymentDrawerSuccessAtom, StorageKeys } from '@ibk/shared-utils';
export const paymentDrawerAtom = atom(false);
export const paymentDrawerItemAtom = atom(null);
export const canApprovePaymentLinksAtom = atom(true);
export const useDrawerPayment = () => {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const [paymentDrawerVisible, setPaymentDrawerVisible] = useAtom(paymentDrawerAtom);
    const [paymentDrawerItem, setPaymentDrawerItem] = useAtom(paymentDrawerItemAtom);
    const [canApprovePaymentLinks, setCanApprovePaymentLinks] = useAtom(canApprovePaymentLinksAtom);
    const [pinModalVisible, setPinModalVisible] = useState(false);
    const [pinAction, setPinAction] = useState(null);
    const [isApproving, setIsApproving] = useState(false);
    const [isReproving, setIsReproving] = useState(false);
    const [isReproveConfirmModalOpen, setIsReproveConfirmModalOpen] = useState(false);
    const [pinKeyboardKey, setPinKeyboardKey] = useState(0);
    const setSuccessMessage = useSetAtom(paymentDrawerSuccessAtom);
    const setErrorMessage = useSetAtom(paymentDrawerErrorAtom);
    const paymentDrawerError = useAtomValue(paymentDrawerErrorAtom);
    const paymentDrawerSuccess = useAtomValue(paymentDrawerSuccessAtom);
    const [approvePaymentMutation] = useApprovePaymentInstructionMutation();
    const [reprovePaymentMutation] = useReprovePaymentInstructionMutation();
    const [showPixLimitExceededModal, setShowPixLimitExceededModal] = useState(false);
    const isPixLimitExceededModalVisible = useMemo(() => {
        return isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_PIX_LIMITS) && showPixLimitExceededModal;
    }, [showPixLimitExceededModal]);
    const errorsThatChangeState = [
        ApiErrors.BILLET_EXPIRED,
        ApiErrors.ERROR_BILLET_EXPIRED,
        ApiErrors.BILLET_ALREADY_PAID,
        ApiErrors.ERROR_BILLET_ALREADY_PAID,
        ApiErrors.INSTRUCTION_NOT_PENDING,
    ];
    const shouldCloseDrawerAndRefresh = useCallback((resultError, errorDetailsCode, errorStatus) => {
        return ((resultError !== undefined && errorsThatChangeState.includes(resultError)) ||
            (errorDetailsCode !== undefined && errorsThatChangeState.includes(errorDetailsCode)) ||
            (errorStatus !== undefined && errorStatus === 502));
    }, []);
    const handleCloseDrawerAndRefresh = useCallback(async () => {
        await localStorageService.removeItem(StorageKeys.PendingPaymentsList);
        window.dispatchEvent(new CustomEvent('payment-approved'));
        setTimeout(() => {
            setPaymentDrawerVisible(false);
            setPaymentDrawerItem(null);
        }, 500);
    }, [setPaymentDrawerVisible, setPaymentDrawerItem]);
    const handleOpenDrawer = useCallback((item, canApprove = true) => {
        setPaymentDrawerItem(item);
        setCanApprovePaymentLinks(canApprove);
        setPaymentDrawerVisible(true);
    }, [setPaymentDrawerVisible, setPaymentDrawerItem, setCanApprovePaymentLinks]);
    const handleCloseDrawer = useCallback(() => {
        setPaymentDrawerVisible(false);
        setPaymentDrawerItem(null);
    }, [setPaymentDrawerVisible, setPaymentDrawerItem]);
    const handleOpenPinModal = useCallback((action) => {
        setPinAction(action);
        setPinModalVisible(true);
    }, []);
    const handleClosePinModal = useCallback(() => {
        setPinModalVisible(false);
        setPinAction(null);
    }, []);
    const handleClosePaymentDrawerError = useCallback(() => {
        setErrorMessage(undefined);
    }, [setErrorMessage]);
    const handleClosePaymentDrawerSuccess = useCallback(() => {
        setSuccessMessage(undefined);
    }, [setSuccessMessage]);
    const approvePaymentInstruction = useCallback(async (instructionId, pin) => {
        setErrorMessage(undefined);
        setIsApproving(true);
        try {
            await approvePaymentMutation({
                instructionId,
                body: { credential: pin },
            }).unwrap();
            setSuccessMessage(t('payment.drawer.toast-approve-success'));
            await localStorageService.removeItem(StorageKeys.PendingPaymentsList);
            window.dispatchEvent(new CustomEvent('payment-approved'));
            setTimeout(() => {
                setPaymentDrawerVisible(false);
                setPaymentDrawerItem(null);
            }, 500);
        }
        catch (err) {
            const error = err;
            const resultError = error?.details?.result?.error;
            const errorDetailsCode = error?.details?.result?.code;
            const errorCode = error?.code;
            const errorStatus = error?.status;
            if (resultError === ApiErrors.PIX_LIMIT_EXCEEDED || errorDetailsCode === ApiErrors.PIX_LIMIT_EXCEEDED) {
                throw error;
            }
            if (resultError === ApiErrors.PIN_FAILURE_TO_VALIDATE) {
                setErrorMessage(t('payment.drawer.toast-pin-invalid'));
                setPinKeyboardKey(prev => prev + 1);
                throw error;
            }
            else if (errorCode === 'REQUEST_TIMEOUT' || error?.status === 408) {
                setErrorMessage(t('payment.drawer.toast-timeout-error'));
            }
            else if (resultError === ApiErrors.NO_FUNDS_AVAILABLE ||
                errorDetailsCode === ApiErrors.ERROR_NO_FUNDS_AVAILABLE) {
                setErrorMessage(t('payment.drawer.toast-no-funds'));
            }
            else if (resultError === ApiErrors.CAN_NOT_PAY_BILLET) {
                setErrorMessage(t('payment.drawer.toast-billet-error'));
            }
            else if (resultError === ApiErrors.BILLET_EXPIRED || errorDetailsCode === ApiErrors.ERROR_BILLET_EXPIRED) {
                setErrorMessage(t('payment.drawer.toast-billet-expired'));
            }
            else if (resultError === ApiErrors.BILLET_ALREADY_PAID ||
                errorDetailsCode === ApiErrors.ERROR_BILLET_ALREADY_PAID) {
                setErrorMessage(t('payment.drawer.toast-billet-already-paid'));
            }
            else if (resultError === ApiErrors.INSTRUCTION_NOT_PENDING) {
                setErrorMessage(t('payment.drawer.toast-instruction-not-pending'));
            }
            else {
                setErrorMessage(t('payment.drawer.toast-approve-error'));
            }
            if (shouldCloseDrawerAndRefresh(resultError, errorDetailsCode, errorStatus)) {
                await handleCloseDrawerAndRefresh();
            }
        }
        finally {
            setIsApproving(false);
        }
    }, [
        approvePaymentMutation,
        setErrorMessage,
        setSuccessMessage,
        setPaymentDrawerVisible,
        setPaymentDrawerItem,
        shouldCloseDrawerAndRefresh,
        handleCloseDrawerAndRefresh,
        t,
    ]);
    const handlePinConfirm = useCallback(async (pin) => {
        if (pinAction === 'approve' && paymentDrawerItem) {
            try {
                await approvePaymentInstruction(paymentDrawerItem.id, pin);
                handleClosePinModal();
            }
            catch (err) {
                const error = err;
                const resultError = error?.details?.result?.error;
                const errorDetailsCode = error?.details?.result?.code;
                if (resultError === ApiErrors.PIX_LIMIT_EXCEEDED || errorDetailsCode === ApiErrors.PIX_LIMIT_EXCEEDED) {
                    setShowPixLimitExceededModal(true);
                }
                if (resultError !== ApiErrors.PIN_FAILURE_TO_VALIDATE) {
                    handleClosePinModal();
                }
            }
        }
        else {
            handleClosePinModal();
        }
    }, [pinAction, paymentDrawerItem, approvePaymentInstruction, handleClosePinModal]);
    const handleOpenReproveConfirmModal = useCallback(() => {
        setIsReproveConfirmModalOpen(true);
    }, []);
    const handleCloseReproveConfirmModal = useCallback(() => {
        setIsReproveConfirmModalOpen(false);
    }, []);
    const handleConfirmReprove = useCallback(async () => {
        if (!paymentDrawerItem)
            return;
        setIsReproving(true);
        setErrorMessage(undefined);
        try {
            await reprovePaymentMutation({
                instructionId: paymentDrawerItem.id,
            }).unwrap();
            setSuccessMessage(t('payment.drawer.toast-reprove-success'));
            await localStorageService.removeItem(StorageKeys.PendingPaymentsList);
            window.dispatchEvent(new CustomEvent('payment-reproved'));
            setTimeout(() => {
                setPaymentDrawerVisible(false);
                setPaymentDrawerItem(null);
            }, 500);
        }
        catch (error) {
            setErrorMessage(t('payment.drawer.toast-reprove-error'));
        }
        finally {
            setIsReproving(false);
            handleCloseReproveConfirmModal();
        }
    }, [
        paymentDrawerItem,
        reprovePaymentMutation,
        setErrorMessage,
        setSuccessMessage,
        setPaymentDrawerVisible,
        setPaymentDrawerItem,
        handleCloseReproveConfirmModal,
        t,
    ]);
    const states = {
        paymentDrawerVisible,
        pinModalVisible,
        pinAction,
        paymentDrawerItem,
        isApproving,
        isReproving,
        isReproveConfirmModalOpen,
        paymentDrawerError,
        paymentDrawerSuccess,
        pinKeyboardKey,
        canApprovePaymentLinks,
        showPixLimitExceededModal,
        isPixLimitExceededModalVisible,
    };
    const actions = {
        handleOpenDrawer,
        handleCloseDrawer,
        handleOpenPinModal,
        handleClosePinModal,
        handlePinConfirm,
        approvePaymentInstruction,
        handleClosePaymentDrawerError,
        handleClosePaymentDrawerSuccess,
        handleOpenReproveConfirmModal,
        handleCloseReproveConfirmModal,
        handleConfirmReprove,
        setShowPixLimitExceededModal,
    };
    return {
        states,
        actions,
    };
};
